import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import Button from '../../ui/Button';
import retailerSettlement from '../../../services/retailerSettlement';
import xeroConnectionService from '../../../services/xeroConnection';
import { errorMessage } from '../../../utils/apiResponse';
import type { RemittanceXeroLists, RemittanceXeroSettings as Settings } from '../../../types/retailerSettlement';

/**
 * The Bunnings remittances section of Accounts → Xero (phase 3 of
 * docs/bunnings-remittance-plan.md).
 *
 * Switched on, each claim recorded on /accounts/remittances goes to Xero as a
 * credit note (Volume Rebate) or a bill (Marketing and Consolidation), at
 * Bunnings' own ex-GST and GST figures, and each recorded remittance is posted
 * through a clearing bank account so that one transfer is the deposit.
 *
 * It needs two permissions the ordinary sign-in does not ask for (payments and
 * bank transfers), so the first time it is set up Xero is reconnected with
 * them. Any accounts seat reads this; an Accounts Admin changes it.
 */

interface Props {
  connected: boolean;
  /** The connection reaches the books. */
  ready: boolean;
  isAdmin: boolean;
}

const INPUT = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';
const LABEL = 'block text-sm font-medium text-neutral-700 mb-1';

export default function RemittanceXeroSettings({ connected, ready, isAdmin }: Props) {
  const { t } = useTranslation('nursery', { keyPrefix: 'RemittanceXeroSettings' });
  const [settings, setSettings] = useState<Settings | null>(null);
  const [lists, setLists] = useState<RemittanceXeroLists | null>(null);
  const [form, setForm] = useState({ enabled: false, clearing: '', bank: '', services: '', tax: 'INPUT' });
  const [busy, setBusy] = useState<'save' | 'connect' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const show = useCallback((s: Settings) => {
    setSettings(s);
    setForm({
      enabled: s.enabled,
      clearing: s.clearing_account_code ?? '',
      bank: s.bank_account_code ?? '',
      services: s.services_account_code ?? '',
      tax: s.input_tax_type || 'INPUT',
    });
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await retailerSettlement.getXeroSettings();
        if (live && res.success && res.data) show(res.data);
      } catch (e) {
        if (live) setError(errorMessage(e, t('loadFailed')));
      }
      if (!isAdmin || !ready) return;
      try {
        const res = await retailerSettlement.getXeroLists();
        if (live && res.success && res.data) setLists(res.data);
      } catch (e) {
        if (live) setError(errorMessage(e, t('listsFailed')));
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only supplies the fallback messages
  }, [isAdmin, ready, show]);

  const save = async () => {
    setBusy('save');
    setError(null);
    setNotice(null);
    try {
      const res = await retailerSettlement.saveXeroSettings({
        enabled: form.enabled,
        clearing_account_code: form.clearing || null,
        bank_account_code: form.bank || null,
        services_account_code: form.services || null,
        input_tax_type: form.tax || null,
      });
      if (res.success && res.data) {
        show(res.data);
        setNotice(res.data.enabled
          ? (res.data.can_post
            ? t('saved.posting')
            : t('saved.needsReconnect'))
          : t('saved.off'));
      } else setError(errorMessage(res, t('saveFailed')));
    } catch (e) {
      setError(errorMessage(e, t('saveFailed')));
    } finally {
      setBusy(null);
    }
  };

  const reconnect = async () => {
    setBusy('connect');
    try {
      const res = await xeroConnectionService.getAuthorizeUrl(true);
      if (res.success && res.data?.authorization_url) window.location.href = res.data.authorization_url;
      else setError(errorMessage(res, t('signInFailed')));
    } catch (e) {
      setError(errorMessage(e, t('signInFailed')));
    } finally {
      setBusy(null);
    }
  };

  const account = (code: string | null, list: { code: string; name: string }[] | undefined) => {
    if (!code) return t('notChosen');
    const a = list?.find((x) => x.code === code);
    return a ? `${a.code} — ${a.name}` : code;
  };

  const select = (id: string, label: string, value: string, onChange: (v: string) => void,
    options: { code: string; name: string }[], help: string) => (
    <div className="min-w-0">
      <label htmlFor={id} className={LABEL}>{label}</label>
      <select id={id} value={value} className={INPUT} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t('choose')}</option>
        {value && !options.some((o) => o.code === value) && <option value={value}>{value}</option>}
        {options.map((o) => <option key={o.code} value={o.code}>{o.code} — {o.name}</option>)}
      </select>
      <p className="mt-1 text-xs text-neutral-500 break-words">{help}</p>
    </div>
  );

  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
      <h2 className="text-base font-semibold text-neutral-900 break-words">{t('title')}</h2>
      <p className="text-sm text-neutral-700 break-words">
        <Trans t={t} i18nKey="intro" components={{ link: <Link className="text-primary-700" to="/accounts/remittances" /> }} />
      </p>
      <ul className="text-sm text-neutral-700 list-disc pl-5 space-y-1 break-words">
        <li>
          <Trans t={t} i18nKey="points.volume" components={{ strong: <strong /> }} />
        </li>
        <li>
          <Trans t={t} i18nKey="points.marketing" components={{ strong: <strong /> }} />
        </li>
        <li>
          {t('points.gst')}
        </li>
        <li>{t('points.fromNow')}</li>
      </ul>

      {!connected ? (
        <p className="text-sm text-neutral-500 break-words">{t('notConnected')}</p>
      ) : !ready ? (
        <p className="text-sm text-neutral-500 break-words">{t('notReady')}</p>
      ) : settings && (
        <>
          {notice && <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 break-words" role="status">{notice}</p>}
          {error && <p role="alert" className="text-sm text-red-700 break-words">{error}</p>}

          {settings.enabled && !settings.can_post && (
            <div className="space-y-2">
              <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 break-words">
                {t('needsPermissions')}
              </p>
              {isAdmin && (
                <Button size="sm" onClick={reconnect} disabled={busy === 'connect'}>
                  {busy === 'connect' ? t('openingXero') : t('reconnect')}
                </Button>
              )}
            </div>
          )}

          {settings.problems.length > 0 && settings.enabled && (
            <p className="text-sm text-amber-900 break-words">{t('stillNeeded', { list: settings.problems.join(', ') })}</p>
          )}

          {isAdmin ? (
            <>
              <label className="flex items-start gap-2 text-sm text-neutral-800">
                <input type="checkbox" className="mt-0.5 h-4 w-4" checked={form.enabled}
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />
                <span className="min-w-0 break-words">{t('enable')}</span>
              </label>
              {select('remit-clearing', t('clearing.label'), form.clearing, (v) => setForm((f) => ({ ...f, clearing: v })),
                lists?.bank_accounts ?? [],
                t('clearing.help'))}
              {select('remit-bank', t('bank.label'), form.bank, (v) => setForm((f) => ({ ...f, bank: v })),
                lists?.bank_accounts ?? [],
                t('bank.help'))}
              {select('remit-services', t('services.label'), form.services, (v) => setForm((f) => ({ ...f, services: v })),
                lists?.expense_accounts ?? [],
                t('services.help'))}
              <div className="min-w-0">
                <label htmlFor="remit-tax" className={LABEL}>{t('taxLabel')}</label>
                <select id="remit-tax" value={form.tax} className={INPUT} onChange={(e) => setForm((f) => ({ ...f, tax: e.target.value }))}>
                  {!(lists?.tax_rates ?? []).some((r) => r.tax_type === form.tax) && (
                    <option value={form.tax}>{form.tax === 'INPUT' ? 'GST on Expenses (INPUT)' : form.tax}</option>
                  )}
                  {(lists?.tax_rates ?? []).map((r) => <option key={r.tax_type} value={r.tax_type}>{r.name} ({Number(r.rate)}%)</option>)}
                </select>
                <p className="mt-1 text-xs text-neutral-500 break-words">
                  {t('taxHelp')}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={save} disabled={busy === 'save'}>{busy === 'save' ? t('saving') : t('save')}</Button>
              </div>
            </>
          ) : (
            <>
              <dl className="space-y-2">
                {[
                  [t('readOnly.posted'), settings.enabled ? t('readOnly.yes') : t('readOnly.no')],
                  [t('readOnly.clearing'), account(settings.clearing_account_code, lists?.bank_accounts)],
                  [t('readOnly.deposit'), account(settings.bank_account_code, lists?.bank_accounts)],
                  [t('readOnly.services'), account(settings.services_account_code, lists?.expense_accounts)],
                ].map(([k, v]) => (
                  <div key={k} className="min-w-0">
                    <dt className="text-xs text-neutral-500">{k}</dt>
                    <dd className="text-sm text-neutral-800 break-words">{v}</dd>
                  </div>
                ))}
              </dl>
              <p className="text-sm text-neutral-500 break-words">{t('adminCanChange')}</p>
            </>
          )}

          {settings.enabled && (
            <p className="text-sm text-neutral-600 break-words">
              {t('waiting', {
                claims: t('claims', { count: settings.queue.claims_pending }),
                remittances: t('remittances', { count: settings.queue.remittances_pending }),
              })}
              {(settings.queue.claims_failed + settings.queue.remittances_failed) > 0 && (
                <>{' '}<Trans t={t} i18nKey="failed" values={{ n: settings.queue.claims_failed + settings.queue.remittances_failed }}
                  components={{ link: <Link className="text-primary-700" to="/accounts/remittances" /> }} /></>
              )}
            </p>
          )}
        </>
      )}
    </section>
  );
}
